import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, ArrowRight, Loader2 } from 'lucide-react';
import { base44 } from '@/api/base44Client';

const INTERESTS = ['Investment', 'Strategic partnership', 'Acquiring a stake in a venture', 'Other'];

export default function ContactSection() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [interest, setInterest] = useState(INTERESTS[0]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await base44.functions.invoke('sendContactEmail', { ...form, message: `Interested in: ${interest}\n\n${form.message}` });
      setSubmitted(true);
      setForm({ name: '', email: '', company: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError('Failed to send message. Please try again or email us directly at info@jotunheims.com');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-32 overflow-hidden">
      {/* Background accent */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left - Philosophy */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col justify-center">
            
            <span className="text-xs font-inter font-medium tracking-[0.3em] uppercase text-primary mb-4 block">
              Partnership Terminal
            </span>
            <h2 className="font-inter font-black text-4xl md:text-5xl tracking-[-0.03em] text-foreground mb-8 leading-tight">
              {"Strategic"}
              <br />
              <span className="text-primary">Partnerships</span>
            </h2>

            <p className="text-muted-foreground font-inter leading-relaxed text-lg mb-8">An investment in Jotunheims is an investment in the critical infrastructure of tomorrow. We bridge the gap between the digital "pipes and wires" of AI and Cybersecurity and the physical necessity of environmental protection. Our comprehensive system integrates advanced thermal-ecological shields that lower home temperatures and slash energy usage, proving that high-tech progress and environmental preservation are inseparable. From Cloud Computing to sustainable energy optimization, Jotunheims is building the infrastructure for a decarbonized, digitally-driven future.

            </p>

            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              {['Software Development', 'AI & Blockchain Integration', 'Quantum Computing', 'Green Energy', 'Cybersecurity', 'Emerging Technologies'].map((item, i) =>
                item ? (
                  <div key={item} className="flex items-center gap-3">
                    <ArrowRight className="w-4 h-4 text-primary shrink-0" />
                    <span className="text-sm font-inter font-medium text-foreground tracking-wide">{item}</span>
                  </div>
                ) : <div key={i} />
              )}
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}>
            
            <div className="rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm p-8">
              {/* Terminal header */}
              






              

              {submitted ?
              <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-12 h-12 rounded-full border-2 border-primary flex items-center justify-center mb-4">
                    <Send className="w-5 h-5 text-primary" />
                  </div>
                  <p className="font-inter font-bold text-foreground mb-1">Transmission Sent</p>
                  <p className="text-sm text-muted-foreground">{"We'll be in touch shortly."}</p>
                </div> :

              <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="text-xs font-inter font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                      Full Name
                    </label>
                    <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-secondary/50 border border-border/50 text-foreground font-inter text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
                    placeholder="Enter your name" />
                  
                  </div>
                  <div>
                    <label className="text-xs font-inter font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                      Email
                    </label>
                    <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-secondary/50 border border-border/50 text-foreground font-inter text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
                    placeholder="you@company.com" />
                  
                  </div>
                  <div>
                    <label className="text-xs font-inter font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                      Company
                    </label>
                    <input
                    type="text"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-secondary/50 border border-border/50 text-foreground font-inter text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
                    placeholder="Your organization" />
                  
                  </div>
                  <div>
                    <label htmlFor="partner-interest" className="text-xs font-inter font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                      I'm interested in
                    </label>
                    <select
                    id="partner-interest"
                    value={interest}
                    onChange={(e) => setInterest(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-secondary/50 border border-border/50 text-foreground font-inter text-sm focus:outline-none focus:border-primary/50 transition-all">
                      {INTERESTS.map((o) => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-inter font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                      Message
                    </label>
                    <textarea
                    rows={4}
                    required
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-secondary/50 border border-border/50 text-foreground font-inter text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all resize-none"
                    placeholder="Describe your partnership interest..." />
                  
                  </div>
                  {error && (
                    <p className="text-sm text-destructive font-inter">{error}</p>
                  )}
                  <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-primary text-primary-foreground font-inter font-semibold text-sm tracking-wider uppercase rounded-lg hover:bg-primary/90 transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /><span>Sending...</span></>
                    ) : (
                      <><span>Start the Conversation</span><Send className="w-4 h-4" /></>
                    )}
                  </button>
                </form>
              }
            </div>
          </motion.div>
        </div>
      </div>
    </section>);

}